import { FormHelperText, Grid, Box, Accordion, AccordionSummary, Typography, AccordionDetails, FormControl, FormControlLabel, Checkbox, 
  Stack, Button, TextField as MuiTextField} from "@mui/material";
import { useRecordContext, useEditContext } from "ra-core";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FC, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import WithSave from "@/controller/WithSave";
import { GeneralSettingDto } from "@/gen/api/securefile";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import Edit from "@/controller/Edit";


interface SfSettings {
  efsEnabled: boolean;
  offlineEnabled: boolean;
  offlineAutoRemove: boolean;
  maxOfflineTimes: number;
  maxOfflineDays: number;
  dataAnalyzerOnpremise: boolean;
  submit: any;
}

const basicResource = "settings-clients-securefile-config"

const BasicSettings: FC = () => {
  const savedHandlers = useDefaultSavedHandlers("Save SecureFile Settings")
  
  return (
    <Edit resource={basicResource} basePath="" id="edit" {...savedHandlers} mutationMode="pessimistic">
      <BasicSettingsBody />
    </Edit>
  )
}

const initialVal = (record: GeneralSettingDto | undefined) => ({
  settings: {
    efsEnabled: record?.efsEnabled ?? true,
    offlineEnabled: record?.offlineEnabled ?? false,
    offlineAutoRemove: record?.offlineAutoRemove ?? true,
    maxOfflineTimes: record?.maxOfflineTimes ?? 200,
    maxOfflineDays: record?.maxOfflineDays ?? 30,
    dataAnalyzerOnpremise: record?.dataAnalyzerOnpremise ?? true,
  },
  tmpRootFolder: '',
  touched: null as {[k in keyof SfSettings]+?: boolean}|null,
  errors: null as {[k in keyof SfSettings]+?: string}|null,
})

const BasicSettingsBody: FC = () => {
  const record : GeneralSettingDto|undefined = useRecordContext() as any
  const { loaded } = useEditContext()
  const [states, setStates] = useState(initialVal(record))
  useEffect(() => {
    if (record) {
      setStates(initialVal(record))
    }
  }, [record])

  const validate = (s1: typeof states) => {
    const errors: any[] = []
    if (s1.settings.offlineEnabled && !s1.settings.maxOfflineTimes) {
      errors.push({ maxOfflineTimes: 'Maximum Offline Operations is required' })
    }
    if (s1.settings.offlineEnabled && !s1.settings.maxOfflineDays) {
      errors.push({ maxOfflineDays: 'Maximum Offline Validity Period is required' })
    }
    return errors.length === 0 ? null : errors.reduce((acc, x) => ({...acc, ...x}), {})
  }

  const history = useHistory()
  useEffect(() => {
    const unregister = history.block((newLocation, a) => {
      const pass = !states.touched
      return a === 'PUSH' && !pass ? 'There are unsaved edits, discard edits and continue?' : undefined
    })
    return () => {
      unregister()
    }
  }, [states, record, history])

  return (
    <>
      {states.errors?.submit && (
        <Box sx={{ mt: 3 }}>
          <FormHelperText error>
            {states.errors?.submit}
          </FormHelperText>
        </Box>
      )}
      <Stack direction="row" sx={{ pb: 2 }} >
        <Button sx={{ ml: 'auto', mr: 2 }}
          color="primary"
          variant="outlined"
          onClick={() => { setStates(initialVal(record)) }}
        >
          Discard
        </Button>
        <WithSave 
          render={({ save, saving }) =>
            <Button
              color="primary"
              disabled={!states.touched || saving}
              type="submit"
              variant="contained"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                setStates(v => {
                  const v1 = {...v,
                    touched: {
                      efsEnabled: true,
                      offlineEnabled: true,
                      offlineAutoRemove: true,
                      maxOfflineTimes: true,
                      maxOfflineDays: true,
                      dataAnalyzerOnpremise: true,
                    },
                  }
                  v1.errors = validate(v1)
                  if (!v1.errors) {  
                    setTimeout(() => {
                      save({...v1.settings}, {
                        onSuccess: () => {
                          setStates(v2 => ({...v2, touched: null}))
                        },
                        onFailure: (err:any) => {
                          setStates(v2 => ({...v2, errors: { submit: err.message }}))
                        },
                      })
                    })
                  }
                  return v1
                })
                return false
              }}
            >
              Save
            </Button>
          }
        />
      </Stack>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box sx={{ m: '1px', '& div.MuiPaper-root.MuiAccordion-root': { borderRadius: 'unset' } }}>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel0-content"
                id="panel0-header"
              >
                <Typography variant="body1" sx={{ mb: 1, color: theme => theme.palette.primary.light }}>
                  Microsoft Encrypted File System</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl sx={{ mt: -2 }}>
                  <FormControlLabel 
                    control={
                      <Checkbox color="primary" name="cbEfsEnabled"
                        checked={states.settings.efsEnabled}
                        onChange={(e) => setStates(v => ({...v, 
                          touched: {...v.touched, efsEnabled: true},
                          settings: {...v.settings, efsEnabled: e.target.checked}
                        }))}
                      />
                    }
                    label="Using Microsoft Encrypted File System"
                  />
                  <FormHelperText>
                    Whether or not the SecureFile app should expect to use the EFS (Encrypted File System) function provided on the client machine. 
                  </FormHelperText>
                </FormControl>
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel2-content"
                id="panel2-header"
              >
                <Typography variant="body1" sx={{ mb: 1, color: theme => theme.palette.primary.light }}>
                  Offline Access</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {loaded &&
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl>
                        <FormControlLabel
                          label="Enable Offline Access"
                          control={
                            <Checkbox checked={states.settings.offlineEnabled} 
                              onChange={(e) => setStates(v => ({...v,
                                touched: {...v.touched, offlineEnabled: true},
                                settings: {...v.settings, offlineEnabled: e.target.checked}
                              }))}
                            />
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl>
                        <FormControlLabel
                          label="Auto Remove Offline Access (when system is back online)"
                          control={
                            <Checkbox checked={states.settings.offlineAutoRemove} 
                              onChange={(e) => setStates(v => ({...v,
                                touched: {...v.touched, offlineAutoRemove: true},
                                settings: {...v.settings, offlineAutoRemove: e.target.checked}
                              }))} 
                            />
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 2, mt: 1 }}>
                      <MuiTextField label="Maximum Offline Operations per file" name="maxOfflineTimes" type="number" fullWidth
                        value={states.settings.maxOfflineTimes}
                        onChange={(e) => {
                          let i : number|'' = Number.parseInt(e.target.value)
                          i = isNaN(i) ? '' : i < 1 ? 1 : i > 32767 ? 32767 : i
                          setStates(v => ({...v,
                            settings: {...v.settings, maxOfflineTimes: i as any },
                            touched: {...v.touched, maxOfflineTimes: true},
                            errors:
                              i === '' && v.settings.offlineEnabled ? {...v.errors, maxOfflineTimes: 'Maximum Offline Operations is required'}
                              : v.errors?.maxOfflineTimes ? (delete v.errors.maxOfflineTimes, v.errors) : v.errors
                          }))
                        }}
                        error={Boolean(states.touched?.maxOfflineTimes && states.errors?.maxOfflineTimes)}
                        helperText={states.errors?.maxOfflineTimes}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 2 }}>
                      <MuiTextField label="Maximum Offline Validity Period in days" name="maxOfflineDays" type="number" fullWidth
                        value={states.settings.maxOfflineDays}
                        onChange={(e) => {
                          let i : number|'' = Number.parseInt(e.target.value)
                          i = isNaN(i) ? '' : i < 1 ? 1 : i > 32767 ? 32767 : i
                          setStates(v => ({...v,
                            settings: {...v.settings, maxOfflineDays: i as any },
                            touched: {...v.touched, maxOfflineDays: true},
                            errors:
                              i === '' && v.settings.offlineEnabled ? {...v.errors, maxOfflineDays: 'Maximum Offline Validity Period is required'}
                              : v.errors?.maxOfflineDays ? (delete v.errors.maxOfflineDays, v.errors) : v.errors
                          }))
                        }}
                        error={Boolean(states.touched?.maxOfflineDays && states.errors?.maxOfflineDays)}
                        helperText={states.errors?.maxOfflineDays}
                      />
                    </Grid>
                  </Grid>}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Grid>
      </Grid>
    </>
  )
}

export default BasicSettings;
